import React from "react";
import { Link } from "react-router-dom";
import { Shield } from "lucide-react";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const services = [
    { label: "Image Forensic Analyzer", href: "https://exif.traxelon.com/" },
    { label: "Email Header Analyzer", href: "https://header.traxelon.com/" },
    { label: "Subdomain Finder", href: "https://sub.traxelon.com/" },
    { label: "Steganography Toolkit", href: "https://steg.traxelon.com/" },
    { label: "Crypto Toolkit", href: "https://crypt.traxelon.com/" },
    { label: "Tech Stack Scanner", href: "https://built.traxelon.com/" },
    { label: "Password Analyzer", href: "https://password.traxelon.com/" },
  ];

  const resources = [
    { label: "Blog & Threat Intel", to: "/blog" },
    { label: "Officer Dashboard", to: "/dashboard" },
    { label: "Voucher & Credits", to: "/vouchers" },
    { label: "Sign Up · Free Trial", to: "/signup" },
  ];

  const company = [
    { label: "About Traxelon", to: "/about" },
    { label: "Contact Support", to: "/contact" },
    { label: "Our Partners", to: "/about" },
    { label: "Terms of Service", to: "/terms" },
  ];

  const partners = [
    { label: "TorSecure Cyber LLP", href: "https://www.torsecure.com/" },
    { label: "Surepass", href: "https://thesurepass.com/" },
  ];

  const renderCol = (title, links) => (
    <div className="flex flex-col gap-4">
      <h4 className="font-display text-sm md:text-base text-white uppercase tracking-widest">
        {title}
      </h4>
      <ul className="flex flex-col gap-2.5">
        {links.map((l, i) => {
          const content = (
            <span className="font-body text-[14px] leading-relaxed text-text-secondary transition-colors duration-200 group-hover:text-primary group-hover:translate-x-1 inline-block">
              {l.label}
            </span>
          );
          return (
            <li key={i} className="group">
              {l.href ? (
                <a href={l.href} target="_blank" rel="noreferrer noopener" className="block">
                  {content}
                </a>
              ) : (
                <Link to={l.to} className="block">
                  {content}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <footer className="relative w-full border-t border-surface-border bg-surface-elevated/40 backdrop-blur-2xl overflow-hidden">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-40 left-1/4 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] opacity-40" />
      <div className="pointer-events-none absolute -bottom-40 right-1/4 w-[500px] h-[500px] bg-violet-500/10 rounded-full blur-[120px] opacity-40" />

      <div className="relative max-w-7xl mx-auto px-6 py-16">
        {/* Top scanline accent */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-10 gap-10 lg:gap-8">
          {/* === COL 1: BRAND + BLURB === */}
          <div className="lg:col-span-3 flex flex-col gap-6">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <Shield className="w-9 h-9 text-primary" />
                <div className="absolute inset-0 w-9 h-9 text-primary opacity-50 blur-md group-hover:opacity-100 transition-opacity">
                  <Shield className="w-9 h-9" />
                </div>
              </div>
              <span className="font-display text-2xl md:text-[28px] text-text-primary tracking-widest leading-none">
                TRAX<span className="text-primary" style={{ textShadow: "0 0 30px rgba(0,212,255,0.45)" }}>ELON</span>
              </span>
            </Link>

            <p className="font-body text-[14.5px] leading-[1.85] text-text-secondary max-w-sm">
              Empowering law enforcement, investigators, and defence personnel with
              real-time intelligence, OSINT-grade forensics, and auditable tracking
              infrastructure — engineered for the future of cyber investigations.
            </p>
          </div>

          {/* === COL 2: OUR SERVICES === */}
          <div className="lg:col-span-2">
            {renderCol("Our Services", services)}
          </div>

          {/* === COL 3: RESOURCES === */}
          <div className="lg:col-span-2">
            {renderCol("Resources", resources)}
          </div>

          {/* === COL 4: COMPANY === */}
          <div className="lg:col-span-2">
            {renderCol("Company", company)}
          </div>

          {/* === COL 5: PARTNERS === */}
          <div className="lg:col-span-1">
            {renderCol("Partners", partners)}
          </div>
        </div>

        {/* === BOTTOM BAR: COPYRIGHT + LEGAL === */}
        <div className="relative mt-16 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-body text-xs text-text-muted">
            © {currentYear} Traxelon · A venture of{" "}
            <a
              href="https://www.torsecure.com/"
              target="_blank"
              rel="noreferrer noopener"
              className="text-text-muted hover:text-primary transition-colors border-b border-transparent hover:border-primary/40"
            >
              TorSecure Cyber LLP
            </a>
            . All rights reserved.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link to="/about" className="font-body text-xs text-text-muted hover:text-primary transition-colors">
              About
            </Link>
            <Link to="/contact" className="font-body text-xs text-text-muted hover:text-primary transition-colors">
              Contact
            </Link>
            <Link to="/blog" className="font-body text-xs text-text-muted hover:text-primary transition-colors">
              Blog
            </Link>
            <Link to="/terms" className="font-body text-xs text-text-muted hover:text-primary transition-colors">
              Terms
            </Link>
            <Link to="/tools" className="font-body text-xs text-text-muted hover:text-primary transition-colors">
              Tools
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
